import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  Close as CloseIcon,
  Warning as WarningIcon,
  KeyboardArrowDown as ArrowDownIcon,
  KeyboardArrowUp as ArrowUpIcon,
} from '@mui/icons-material';
import { axiosPrivate } from '../../../api/axios';
import { SectionRenderer } from './machineWidgets';

// --- Default Config (fallback when API fails) ---
import { getDefaultConfig } from './machineWidgets/defaultConfig';

/**
 * StatusBadge - Machine status indicator
 */
const StatusBadge = ({ status, onStatusClick }) => {
  const statusList = [
    { id: 1, label: '가동중', color: 'emerald', originalIds: [1, 2, 3, 4, 5, 6, 10] },
    { id: 2, label: '정비중', color: 'orange', originalIds: [8, 11] },
    { id: 3, label: '운휴', color: 'red', originalIds: [9] }
  ];

  const mapping = statusList.find(s => s.originalIds.includes(status));
  const normalizedStatus = mapping ? mapping.id : 3;
  const isViolation = status === 10 || status === 11;
  const isMaintenanceViolation = status === 11;

  return (
    <div className="flex gap-2">
      {statusList.map((item) => {
        const isActive = normalizedStatus === item.id;
        const colorStyles = {
          emerald: { active: 'text-emerald-400 bg-emerald-500/20 border-emerald-500/30', glow: '0 0 15px rgba(16,185,129,0.3)', dot: 'bg-emerald-500' },
          orange: { active: 'text-orange-400 bg-orange-500/20 border-orange-500/30', glow: '0 0 15px rgba(245,158,11,0.3)', dot: 'bg-orange-500' },
          red: { active: 'text-red-400 bg-red-500/20 border-red-500/30', glow: '0 0 15px rgba(239,68,68,0.3)', dot: 'bg-red-500' }
        };
        const currentStyle = colorStyles[item.color];

        return (
          <div
            key={item.id}
            onClick={() => {
              if (isActive && isMaintenanceViolation && onStatusClick) {
                onStatusClick();
              }
            }}
            className={`
              flex items-center gap-1.5 px-2.5 py-1 rounded-lg border transition-all duration-500
              ${isActive
                ? `${currentStyle.active} opacity-100 scale-100 ${isMaintenanceViolation ? 'cursor-pointer hover:brightness-125' : ''}`
                : 'bg-white/5 text-slate-600 border-white/5 opacity-30 scale-95 grayscale'}
            `}
            style={{
              boxShadow: isActive ? currentStyle.glow : 'none',
              backdropFilter: isActive ? 'blur(4px)' : 'none'
            }}
          >
            <div className={`w-1.5 h-1.5 rounded-full ${isActive ? currentStyle.dot : 'bg-slate-700'}`} />
            <span className="text-[10px] font-black tracking-tighter uppercase whitespace-nowrap flex items-center gap-1">
              {item.label}
              {isActive && isViolation && (
                <WarningIcon sx={{ fontSize: 12, color: 'inherit' }} />
              )}
            </span>
          </div>
        );
      })}
    </div>
  );
};

/**
 * Popup configuration generator for history popups
 */
const getPopupConfig = (type, mchData) => {
  const configs = {
    riskStatus: {
      title: '위험성 평가 현황 세부 이력',
      columns: [
        { header: '코드', accessorKey: 'risk_factor_code' },
        {
          header: '위험 레벨', accessorKey: 'risk_danger_level', cell: ({ getValue }) => (
            <span className={`font-black ${getValue() >= 3 ? 'text-red-400' : 'text-orange-400'}`}>LEVEL {getValue()}</span>
          )
        },
        { header: '위험 상황', accessorKey: 'risk_situation' },
        { header: '발생 원인', accessorKey: 'risk_cause' },
      ],
      data: mchData.riskData || []
    },
    riskMeasure: {
      title: '개선대책 수립 현황 세부 이력',
      columns: [
        { header: '코드', accessorKey: 'risk_factor_code' },
        { header: '위험 레벨', accessorKey: 'risk_danger_level' },
        { header: '위험 상황', accessorKey: 'risk_situation' },
        { header: '개선 대책', accessorKey: 'risk_measure' },
      ],
      data: mchData.riskData || []
    },
    accident: {
      title: '설비 사고 및 아차사고 이력',
      columns: [
        { header: '발생 일자', accessorKey: 'broken_date' },
        { header: '사고 내용', accessorKey: 'broken_description' },
        { header: '조치 내용', accessorKey: 'maintenance_description' },
      ],
      data: mchData.brokenLogData || []
    },
    maintenance: {
      title: '설비 보전 및 점검 이력',
      columns: [
        { header: '일자', accessorKey: 'date' },
        { header: '구분', accessorKey: 'type' },
        { header: '상세 내용', accessorKey: 'description' },
      ],
      data: mchData.maintenanceLogData || []
    }
  };
  
  return configs[type] || { title: '', columns: [], data: [] };
};

/**
 * MachineDetailInfo - Dynamic machine detail panel
 * Renders sections based on widget configuration from API
 */
export default function MachineDetailInfo({ 
  isOpen, 
  onClose, 
  data, 
  sendMessage,
  onOpenHistory, 
  onStatusClick, 
  onDownloadCert 
}) {
  const { group_code, project_code } = useParams();
  const scrollRef = useRef(null);
  const contentRef = useRef(null);
  const [showScrollDown, setShowScrollDown] = useState(true);
  const [hasScrollableContent, setHasScrollableContent] = useState(false);
  const [widgetConfig, setWidgetConfig] = useState(null);
  const [dynamicData, setDynamicData] = useState({});

  const mchData = data || {};

  // Panel disabled state (per-machine visibility control)
  const [panelDisabled, setPanelDisabled] = useState(false);

  // Fetch Layout Configuration + Data in single API call
  // Backend handles group+category lookup from mchId internally
  useEffect(() => {
    const fetchLayoutWithData = async () => {
      try {
        const response = await axiosPrivate.post('/dt/mchinfomod/full', {
          mchId: mchData.mchId || null,
          group_code,
          project_code
        });

        // Panel disabled for this specific machine
        if (response.data?.panelDisabled) {
          setPanelDisabled(true);
          setWidgetConfig(null);
          setDynamicData({});
          return;
        }
        
        setPanelDisabled(false);

        // If sections exist, use API response
        if (response.data?.sections?.length > 0) {
          setWidgetConfig({ sections: response.data.sections });
          setDynamicData(response.data.data || {});
        } else {
          // Fallback to default config
          console.warn('No dynamic layout found, using default.');
          setWidgetConfig(getDefaultConfig());
          setDynamicData({});
        }
      } catch (error) {
        console.error('Failed to fetch layout:', error);
        setPanelDisabled(false);
        setWidgetConfig(getDefaultConfig());
        setDynamicData({});
      }
    };

    if (isOpen && group_code && project_code) {
      fetchLayoutWithData();
    }
  }, [isOpen, group_code, project_code, mchData.mchId]);

  // Compute derived data for widgets
  // Uses backend-computed values from dynamicData when available, with local fallback
  const enrichedData = useMemo(() => {
    const riskData = mchData.riskData || [];
    const brokenLogData = mchData.brokenLogData || [];

    // Local fallback calculations (used when backend doesn't provide computed values)
    const localRiskStats = {
      high: riskData.filter(r => r.risk_danger_level >= 3).length,
      mid: riskData.filter(r => r.risk_danger_level === 2).length,
      low: riskData.filter(r => r.risk_danger_level <= 1).length,
    };
    const localMeasureStats = {
      none: riskData.filter(r => !r.risk_measure || r.risk_measure.includes('필요')).length,
      set: riskData.filter(r => r.risk_measure && r.risk_measure.includes('수립')).length,
      done: riskData.filter(r => r.risk_measure && !r.risk_measure.includes('필요') && !r.risk_measure.includes('수립')).length,
    };

    // Merge: mchData base → dynamicData (backend computed) → local fallbacks
    return {
      ...mchData,
      ...dynamicData, // Backend data (source_key as key)
      
      // Risk statistics: prefer backend, fallback to local
      risk_stats: {
        high: dynamicData.risk_count_high ?? localRiskStats.high,
        mid: dynamicData.risk_count_mid ?? localRiskStats.mid,
        low: dynamicData.risk_count_low ?? localRiskStats.low,
      },
      // Measure statistics: prefer backend, fallback to local
      measure_stats: {
        none: dynamicData.measure_count_none ?? localMeasureStats.none,
        set: dynamicData.measure_count_set ?? localMeasureStats.set,
        done: dynamicData.measure_count_done ?? localMeasureStats.done,
      },
      // Accident statistics: prefer backend, fallback to local
      accident_history: {
        accident: dynamicData.accident_count ?? brokenLogData.length,
        near_miss: dynamicData.near_miss_count ?? 0,
      },
      // Maintenance statistics
      maintenance: {
        broken: dynamicData.accident_count ?? brokenLogData.length,
        compliance: dynamicData.maintenance_compliance ?? mchData.maintenance_compliance ?? 100,
      },
      // Safety inspection data
      last_inspection_date: dynamicData.last_inspection_date ?? new Date().toLocaleDateString(),
      inspection_compliance: dynamicData.inspection_compliance ?? mchData.inspection_compliance ?? 98,
      inspection_status: dynamicData.inspection_status ?? mchData.inspection_status ?? 'PASS',
      // Next maintenance date
      next_maintenance_date: dynamicData.next_maintenance_date ?? mchData.next_maintenance_date ?? '2026-02-15',
      maintenance_progress: dynamicData.maintenance_progress ?? mchData.maintenance_progress ?? 72,
      // MSDS data
      msds: {
        ink_cleaning: dynamicData.msds_ink_cleaning ?? mchData.msds_ink_cleaning ?? '아세톤',
        auto_stamp: dynamicData.msds_auto_stamp ?? mchData.msds_auto_stamp ?? '이소프로필 알코올',
      }
    };
  }, [mchData, dynamicData]);

  // Scroll handling
  const checkScroll = useCallback(() => {
    if (scrollRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
      const canScroll = scrollHeight > clientHeight + 4;
      setHasScrollableContent(canScroll);

      if (!canScroll) {
        setShowScrollDown(false);
        return;
      }

      const isBottom = scrollHeight - scrollTop <= clientHeight + 20;
      setShowScrollDown(!isBottom);
    }
  }, []);

  useEffect(() => {
    const frame = window.requestAnimationFrame(checkScroll);
    return () => window.cancelAnimationFrame(frame);
  }, [isOpen, data, widgetConfig, dynamicData, panelDisabled, checkScroll]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleResize = () => checkScroll();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isOpen, checkScroll]);

  useEffect(() => {
    if (!isOpen || !scrollRef.current || !contentRef.current || typeof ResizeObserver === "undefined") {
      return undefined;
    }

    const observer = new ResizeObserver(() => {
      checkScroll();
    });

    observer.observe(scrollRef.current);
    observer.observe(contentRef.current);

    return () => {
      observer.disconnect();
    };
  }, [isOpen, checkScroll]);

  const handleScrollAction = useCallback(() => {
    if (scrollRef.current) {
      if (showScrollDown) {
        scrollRef.current.scrollTo({ top: scrollRef.current.scrollTop + 350, behavior: 'smooth' });
      } else {
        scrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  }, [showScrollDown]);

  // Handle history popup
  const handleOpenHistory = useCallback((popupType) => {
    if (onOpenHistory) {
      const config = getPopupConfig(popupType, mchData);
      onOpenHistory(config);
    }
  }, [onOpenHistory, mchData]);

  // Re-fetch panel data (used after update actions)
  const refetchPanelData = useCallback(async () => {
    try {
      const response = await axiosPrivate.post('/dt/mchinfomod/full', {
        mchId: mchData.mchId || null,
        group_code,
        project_code
      });
      if (response.data?.sections?.length > 0) {
        setWidgetConfig({ sections: response.data.sections });
        setDynamicData(response.data.data || {});
      }
    } catch (error) {
      console.error('Failed to refetch panel data:', error);
    }
  }, [mchData.mchId, group_code, project_code]);

  // Handle widget actions
  const handleAction = useCallback(async (actionType, actionData) => {
    switch (actionType) {
      case 'downloadCert':
        if (onDownloadCert) {
          onDownloadCert(actionData);
        }
        break;
      case 'openMsds':
        // Handle MSDS download - implement as needed
        console.log('Open MSDS:', actionData);
        break;
      case 'updateValue':
        // Update data source value and re-fetch panel
        try {
          await axiosPrivate.post('/dt/mchinfomod/update-value', {
            source_key: actionData.source_key,
            dataKey: actionData.dataKey,
            value: actionData.value,
            mchId: mchData.mchId,
            group_code,
            project_code
          });
          // Re-fetch to get updated data + recalculated dependent values
          await refetchPanelData();
        } catch (error) {
          console.error('Failed to update value:', error);
          throw error; // Propagate to caller for UI feedback
        }
        break;
      default:
        console.log('Unknown action:', actionType, actionData);
    }
  }, [onDownloadCert, mchData.mchId, group_code, project_code, refetchPanelData]);

  // Get sections from config
  const sections = widgetConfig?.sections || [];

  return (
    <div
      id="machine-detail-info"
      className={`
        absolute top-6 right-4 w-[440px] z-50 h-auto max-h-[calc(100vh-3rem)]
        bg-[#0a0f1e]/80 backdrop-blur-3xl rounded-xl
        shadow-[0_40px_80px_-20px_rgba(0,0,0,0.8)]
        flex flex-col overflow-hidden
        transition-[transform,opacity] duration-700 ease-[cubic-bezier(0.2,1,0.2,1)] will-change-transform
        ${isOpen ? 'translate-x-0 opacity-100' : 'translate-x-[480px] opacity-0 pointer-events-none'}
      `}
      style={{ border: '1px solid rgba(255,255,255,0.12)' }}
    >
      {/* Premium Glow backgrounds */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/5 blur-[80px] pointer-events-none rounded-full" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-emerald-500/5 blur-[80px] pointer-events-none rounded-full" />

      {/* Header */}
      <div className="p-8 pb-4 flex justify-between items-start z-10">
        <div>
          <h2 className="text-2xl font-black text-white tracking-tighter leading-tight mb-3">
            {mchData.machine_name || '설비명 미등록'}
          </h2>
          <StatusBadge status={mchData.machine_status || 0} onStatusClick={onStatusClick} />
        </div>
        <button
          onClick={onClose}
          className="p-2.5 rounded-2xl bg-white/5 text-slate-400 hover:text-white hover:bg-white/10 transition-all active:scale-90"
          style={{ border: '1px solid rgba(255,255,255,0.05)' }}
        >
          <CloseIcon sx={{ fontSize: 24 }} />
        </button>
      </div>

      {/* Content - Dynamic Sections */}
      <div
        ref={scrollRef}
        onScroll={checkScroll}
        className="flex-1 min-h-0 overflow-y-auto px-8 pb-4 z-10
          [&::-webkit-scrollbar]:w-1 
          [&::-webkit-scrollbar-thumb]:bg-white/10 
          [&::-webkit-scrollbar-thumb]:rounded-full 
          [&::-webkit-scrollbar-track]:bg-transparent"
      >
        <div ref={contentRef}>
          {panelDisabled ? (
            <div className="flex flex-col items-center justify-center h-full text-center gap-4 py-16">
              <div className="w-14 h-14 rounded-2xl bg-white/5 flex items-center justify-center">
                <CloseIcon sx={{ fontSize: 28, color: 'rgba(148,163,184,0.3)' }} />
              </div>
              <div>
                <p className="text-slate-500 text-[13px] font-bold">상세 정보 패널이 비활성화된 장비입니다</p>
                <p className="text-slate-600 text-[11px] mt-1">관리자에게 문의하세요</p>
              </div>
            </div>
          ) : (
            <>
              {sections.map((section) => (
                <SectionRenderer
                  key={section.id}
                  section={section}
                  data={enrichedData}
                  sendMessage={sendMessage}
                  onOpenHistory={handleOpenHistory}
                  onAction={handleAction}
                />
              ))}
            </>
          )}

          {/* Padding for scroll button */}
          <div className="h-8" />
        </div>
      </div>

      {/* Floating Scroll Indicator / Back to Top */}
      {hasScrollableContent && (
        <button
          onClick={handleScrollAction}
          className="absolute bottom-6 left-1/2 -translate-x-1/2 w-10 h-10 rounded-full bg-slate-900/40 backdrop-blur-md text-slate-400 shadow-[0_8px_20px_rgba(0,0,0,0.4)] flex items-center justify-center hover:bg-slate-800/60 hover:text-white hover:scale-110 active:scale-90 transition-all z-20"
          style={{ border: '1px solid rgba(255,255,255,0.08)' }}
        >
          {showScrollDown ? <ArrowDownIcon /> : <ArrowUpIcon />}
        </button>
      )}
    </div>
  );
}
